import { useMemo } from "react";
import { BenefitsStatementItem } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified/benefits-statement";
import { getBenefitsStatementReasonCodes } from "./utils";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { KeyValue } from "views/applications-id-report/components/KeyValue";
import { formatDate } from "views/applications-id-report/utils";
import { AuthenticityScoreAlert } from "views/applications-id-report/slices/CombinedIncomeReportSlice/components/AuthenticityScoreAlert";
import { DocumentIssueAlert } from "views/applications-id-report/slices/CombinedIncomeReportSlice/components/DocumentIssueAlert";
import { resolveAuthenticityTone } from "views/applications-id-report/slices/CombinedIncomeReportSlice/utils/authenticityTone";
import { Show } from "shared/components";
import {
  resolveAuthenticityScore,
  resolveFraudRisk,
  resolveRiskSummary
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/utils/fraudRisk";
import {
  resolveDocumentProblems,
  toFraudRiskLines
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/utils/documentProblems";
import { NameMismatchAlert } from "../NameMismatchAlert";

export const ExpandedTableRow = ({
  benefitsItem
}: {
  benefitsItem: BenefitsStatementItem;
}) => {
  const reasonCodes = useMemo(
    () => getBenefitsStatementReasonCodes(benefitsItem),
    [benefitsItem]
  );

  const source = benefitsItem.income_source_data;
  const riskLevel = resolveFraudRisk(source);
  const score = resolveAuthenticityScore(source);
  const nameMismatch = benefitsItem.data?.benefits_statement.name_mismatch;

  const problems = useMemo(() => resolveDocumentProblems(source), [source]);

  return (
    <div className="p-6 bg-background-900 space-y-6">
      <div className="space-y-3">
        <Show if={Boolean(nameMismatch)}>
          <NameMismatchAlert nameMismatch={nameMismatch!} />
        </Show>

        <Show if={problems.length > 1}>
          {problems.map((problem) => (
            <DocumentIssueAlert
              key={problem.label}
              label={problem.label}
              description={problem.description}
            />
          ))}
        </Show>

        <AuthenticityScoreAlert
          tone={resolveAuthenticityTone(riskLevel, score)}
          score={score}
          issues={toFraudRiskLines(reasonCodes, resolveRiskSummary(source))}
        />
      </div>

      <ContentTitle
        title="Government benefit letter details"
        contentClassName="space-y-2"
      >
        <KeyValue
          label="Name"
          value={benefitsItem.data?.benefits_statement.name}
        />
        <KeyValue
          label="Address"
          value={benefitsItem.data?.benefits_statement.address}
        />
        <KeyValue
          label="Document date"
          value={formatDate(benefitsItem.data?.benefits_statement.date)}
        />
      </ContentTitle>
    </div>
  );
};
